/**
 * What searching changed (SYR-111 phase 3).
 *
 * Every statement is asked twice: once with the web available, once from what
 * the model already holds. The gap between them is the only part of the read
 * that isolates the PUBLIC RECORD from everything else a model knows — and on
 * the first full read it ran the wrong way, with brands recommended less after
 * searching than from memory.
 *
 * Negative means the record cost you: the models thought better of the brand
 * before they looked.
 */

import React from 'react';
import { Card, Space, Table, Typography } from 'antd';
import { THEME } from '../../config/theme';
import { getDisplayName } from '../../../../utils/elementDisplayNames';

const { Text, Paragraph } = Typography;

interface Props {
  consensus: { result?: any } | null;
  memory: { result?: any } | null;
}

export const MemoryVsSearch: React.FunctionComponent<Props> = ({ consensus, memory }) => {
  const searched = consensus?.result;
  const held = memory?.result;
  if (!searched || !held) return null;

  const heldByName = new Map(
    (held.latentElementScores ?? []).map((e: any) => [e.element, e.score]),
  );

  const rows = (searched.latentElementScores ?? [])
    .map((e: any) => {
      const before = heldByName.get(e.element);
      if (e.score === null || before === null || before === undefined) return null;
      return {
        element: e.element,
        searched: e.score as number,
        memory: before as number,
        delta: (e.score as number) - (before as number),
      };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => Math.abs(b.delta) - Math.abs(a.delta));

  if (rows.length === 0) return null;

  return (
    <Card title="What searching changed">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Table
          size="small"
          pagination={false}
          rowKey="element"
          dataSource={rows.slice(0, 8)}
          columns={[
            {
              title: 'Element',
              dataIndex: 'element',
              render: (name: string) => getDisplayName(name),
            },
            {
              title: 'From memory',
              dataIndex: 'memory',
              width: 130,
              render: (value: number) => value.toFixed(2),
            },
            {
              title: 'After searching',
              dataIndex: 'searched',
              width: 140,
              render: (value: number) => <Text strong>{value.toFixed(2)}</Text>,
            },
            {
              title: 'Change',
              dataIndex: 'delta',
              width: 120,
              render: (delta: number) => (
                <Text style={{ color: delta < 0 ? '#dc2626' : '#059669' }}>
                  {delta > 0 ? '+' : ''}
                  {delta.toFixed(2)}
                </Text>
              ),
            },
          ]}
        />
        <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, fontSize: 13, maxWidth: '72ch' }}>
          A negative change means the models thought better of you before they looked. That is
          the most fixable finding here, because the record is the part you control — and it is
          invisible to your customer survey, which can only report what people already believe.
        </Paragraph>
      </Space>
    </Card>
  );
};
